import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CheckCircle,
  Circle,
  AlertTriangle,
  RefreshCw,
  ListTodo,
  Play,
  MoreVertical,
  Clock,
  Target,
  Users,
  FileText,
  Settings
} from 'lucide-react';
import { useOnboardingTaskInstances } from '@/hooks/useOnboardingTaskInstances';
import { Onboarding } from '@/services/onboarding.service';
import { toast } from 'sonner';

interface OnboardingTasksDialogProps {
  onboarding: Onboarding | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTaskStatusUpdate?: (taskId: string, status: string) => Promise<void>;
  canEditOnboarding?: boolean;
}

export const OnboardingTasksDialog: React.FC<OnboardingTasksDialogProps> = ({
  onboarding,
  open,
  onOpenChange,
  onTaskStatusUpdate,
  canEditOnboarding = false
}) => {
  const { taskInstances, loading, error, fetchTaskInstances } = useOnboardingTaskInstances(onboarding?._id);
  const [updatingTask, setUpdatingTask] = useState<string | null>(null);
  const [optimisticTaskInstances, setOptimisticTaskInstances] = useState<any[]>([]);
  const [showCompleteAllConfirmation, setShowCompleteAllConfirmation] = useState(false);
  const [categoryToComplete, setCategoryToComplete] = useState<string>('');
  const [tasksToComplete, setTasksToComplete] = useState<any[]>([]);


  // Refresh tasks when dialog opens
  useEffect(() => {
    if (open && onboarding?._id) {
      fetchTaskInstances();
    }
  }, [open, onboarding?._id, fetchTaskInstances]);

  // Listen for task creation events
  useEffect(() => {
    const handleTaskCreated = () => {
      if (open && onboarding?._id) {
        toast.info('🔄 New task detected! Refreshing task list...', {
          duration: 2000
        });
        fetchTaskInstances();
      }
    };

    window.addEventListener('task-created', handleTaskCreated);

    return () => {
      window.removeEventListener('task-created', handleTaskCreated);
    };
  }, [open, onboarding?._id, fetchTaskInstances]);

  // Load available tasks for adding to onboarding

  // Handle adding tasks to onboarding

  // Handle opening add tasks dialog

  // Initialize optimistic instances with actual task instances
  useEffect(() => {
    setOptimisticTaskInstances(taskInstances);
  }, [taskInstances]);

  // Fallback: use tasks from onboarding record if API fails
  const fallbackTasks = onboarding?.tasks || [];
  const hasFallbackTasks = fallbackTasks.length > 0;

  // Use optimistic instances for calculations if available, otherwise use actual instances
  const displayInstances = optimisticTaskInstances.length > 0 ? optimisticTaskInstances : taskInstances;

  // Calculate completion statistics
  const totalTasks = displayInstances.length;
  const completedTasks = displayInstances.filter(task => task.status === 'completed').length;
  const pendingTasks = displayInstances.filter(task => !task.status || task.status === 'pending').length;

  const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Group tasks by category
  const tasksByCategory = displayInstances.reduce((acc, task) => {
    const category = task.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(task);
    return acc;
  }, {} as Record<string, typeof displayInstances>);

  // Get unique categories
  const categories = Object.keys(tasksByCategory);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'in_progress':
        return <Play className="h-4 w-4 text-blue-600" />;
      case 'pending':
        return <Circle className="h-4 w-4 text-gray-400" />;
      default:
        return <Circle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'documentation':
        return <FileText className="h-5 w-5" />;
      case 'training':
        return <Target className="h-5 w-5" />;
      case 'setup':
        return <Settings className="h-5 w-5" />;
      case 'meeting':
        return <Users className="h-5 w-5" />;
      default:
        return <ListTodo className="h-5 w-5" />;
    }
  };

  const formatStatus = (status: string) => {
    if (!status) return 'Pending';
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleStatusUpdate = async (taskInstanceId: string, newStatus: string) => {
    if (!onboarding?._id || !onTaskStatusUpdate) return;

    try {
      setOptimisticTaskInstances(prevInstances =>
        prevInstances.map(task =>
          task._id === taskInstanceId
            ? { ...task, status: newStatus }
            : task
        )
      );

      setUpdatingTask(taskInstanceId);
      await onTaskStatusUpdate(taskInstanceId, newStatus);
      toast.success(`Task status updated to ${newStatus}`);

    } catch (error: any) {
      console.error('Error updating task status:', error);
      setOptimisticTaskInstances(prevInstances =>
        prevInstances.map(task =>
          task._id === taskInstanceId
            ? { ...task, status: task.status === 'pending' ? 'pending' : task.status }
            : task
        )
      );
      toast.error('Failed to update task status');
    } finally {
      setUpdatingTask(null);
    }
  };

  const handleCompleteAllInCategory = async (category: string) => {
    if (!onboarding?._id || !onTaskStatusUpdate) return;

    const categoryTasks = tasksByCategory[category];
    const incompleteTasks = categoryTasks.filter((task: any) => task.status !== 'completed');

    if (incompleteTasks.length === 0) {
      toast.info('All tasks in this category are already completed!');
      return;
    }

    setCategoryToComplete(category);
    setTasksToComplete(incompleteTasks);
    setShowCompleteAllConfirmation(true);
  };

  const handleConfirmCompleteAll = async () => {
    if (!onboarding?._id || !onTaskStatusUpdate) return;

    try {
      // Optimistically update all tasks in the category
      setOptimisticTaskInstances(prevInstances =>
        prevInstances.map(task =>
          tasksByCategory[categoryToComplete].some((catTask: any) => catTask._id === task._id)
            ? { ...task, status: 'completed' }
            : task
        )
      );

      // Update all incomplete tasks in the category
      const updatePromises = tasksToComplete.map((task: any) =>
        onTaskStatusUpdate(task._id, 'completed')
      );

      await Promise.all(updatePromises);
      toast.success(`All ${tasksToComplete.length} tasks in "${categoryToComplete}" marked as completed!`);

    } catch (error: any) {
      console.error('Error completing all tasks in category:', error);
      // Revert optimistic updates
      setOptimisticTaskInstances(taskInstances);
      toast.error('Failed to complete all tasks in category');
    } finally {
      setShowCompleteAllConfirmation(false);
      setCategoryToComplete('');
      setTasksToComplete([]);
    }
  };

  const handleCancelCompleteAll = () => {
    setShowCompleteAllConfirmation(false);
    setCategoryToComplete('');
    setTasksToComplete([]);
  };

  const renderErrorContent = () => {
    if (error?.includes('Cannot GET')) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertTriangle className="h-16 w-16 text-red-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">API Endpoint Not Found</h3>
          <p className="text-gray-600 mb-6 max-w-md">
            The tasks endpoint is not available. This might be because no tasks have been assigned to this onboarding yet.
          </p>
          <Button variant="outline" onClick={() => fetchTaskInstances()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertTriangle className="h-16 w-16 text-red-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Tasks</h3>
        <p className="text-gray-600 mb-6">{error}</p>
        <Button variant="outline" onClick={() => fetchTaskInstances()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  };

  if (error) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTodo className="h-6 w-6" />
              Onboarding Tasks
            </DialogTitle>
            <DialogDescription className="sr-only">Error message showing onboarding task could not be loaded.</DialogDescription>
          </DialogHeader>
          {renderErrorContent()}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1000px] max-h-[90vh]">
        <DialogHeader className="border-b pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ListTodo className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">
                  Onboarding Tasks
                </DialogTitle>
                <DialogDescription className="sr-only">Comprehensive list and status tracking of all onboarding tasks for the candidate.</DialogDescription>
                {onboarding?.candidate && (
                  <p className="text-sm text-gray-600">
                    {onboarding.candidate.firstName} {onboarding.candidate.lastName}
                  </p>
                )}
              </div>
            </div>

          </div>
        </DialogHeader>

        <div className="space-y-6 py-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-800 mb-1">Timeline Completion</p>
                    <p className="text-2xl font-bold text-green-900">{completionPercentage}%</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Target className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800 mb-1">Completed</p>
                    <p className="text-2xl font-bold text-blue-900">{completedTasks}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-800 mb-1">Pending</p>
                    <p className="text-2xl font-bold text-orange-900">{pendingTasks}</p>
                  </div>
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Clock className="h-5 w-5 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Progress Overview */}
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Completion Rate</span>
                  <span className="text-lg font-semibold text-gray-900">{completionPercentage}%</span>
                </div>
                <Progress value={completionPercentage} className="h-3" indicatorColor="bg-blue-500" />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>{completedTasks} of {totalTasks} tasks completed</span>
                  <span>{totalTasks - completedTasks} remaining</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tasks by Category */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                <span className="text-gray-600">Loading tasks...</span>
              </div>
            </div>
          ) : taskInstances.length === 0 && !hasFallbackTasks ? (
            <div className="text-center py-12">
              <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <ListTodo className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tasks Assigned</h3>
              <p className="text-gray-600 mb-6">No tasks have been assigned to this onboarding process yet.</p>

            </div>
          ) : displayInstances.length === 0 && hasFallbackTasks ? (
            <div className="text-center py-12">
              <div className="p-4 bg-yellow-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Tasks Found in Record</h3>
              <p className="text-gray-600 mb-4">Tasks are assigned but detailed information is not available.</p>
              <div className="text-sm text-gray-500 bg-gray-100 p-3 rounded-lg">
                Task IDs: {fallbackTasks.join(', ')}
              </div>
            </div>
          ) : (
            <div className="space-y-4">

              {categories.map((category) => {
                const categoryTasks = tasksByCategory[category];
                const categoryCompleted = categoryTasks.filter((task: any) => task.status === 'completed').length;

                return (
                  <Card key={category} className="border border-gray-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            {getCategoryIcon(category)}
                          </div>
                          <div>
                            <CardTitle className="text-lg">{category}</CardTitle>
                            <p className="text-sm text-gray-600">{categoryTasks.length} tasks</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {canEditOnboarding && categoryCompleted < categoryTasks.length && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCompleteAllInCategory(category)}
                              className="ml-2 h-6 px-2 text-xs flex items-center gap-1 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                            >
                              <CheckCircle className="h-3 w-3" />
                              Complete All
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {categoryTasks.map((task: any) => (
                          <div key={task._id} className="group relative p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all duration-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  {getStatusIcon(task.status || 'pending')}
                                  <h5 className="font-medium text-gray-900 truncate">
                                    {task.title || task.taskId || `Task ${task._id || 'Unknown'}`}
                                  </h5>
                                </div>
                                {(task.description || task.title === `Task ${task._id || 'Unknown'}`) && (
                                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                    {task.description || 'Task details not available'}
                                  </p>
                                )}
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className={`text-xs ${getStatusColor(task.status || 'pending')}`}>
                                    {formatStatus(task.status || 'pending')}
                                  </Badge>
                                </div>
                              </div>

                              {canEditOnboarding && (
                                <div className="ml-2">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        disabled={updatingTask === task._id}
                                        className="h-7 w-7 p-0"
                                      >
                                        {updatingTask === task._id ? (
                                          <RefreshCw className="h-3 w-3 animate-spin" />
                                        ) : (
                                          <MoreVertical className="h-3 w-3" />
                                        )}
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {task.status !== 'in_progress' && task.status !== 'completed' && (
                                        <DropdownMenuItem
                                          onClick={() => handleStatusUpdate(task._id, 'in_progress')}
                                          className="flex items-center gap-2"
                                        >
                                          <Play className="h-4 w-4" />
                                          Mark In Progress
                                        </DropdownMenuItem>
                                      )}
                                      {task.status !== 'completed' && (
                                        <DropdownMenuItem
                                          onClick={() => handleStatusUpdate(task._id, 'completed')}
                                          className="flex items-center gap-2"
                                        >
                                          <CheckCircle className="h-4 w-4" />
                                          Mark Complete
                                        </DropdownMenuItem>
                                      )}
                                      {task.status === 'completed' && (
                                        <DropdownMenuItem
                                          onClick={() => handleStatusUpdate(task._id, 'pending')}
                                          className="flex items-center gap-2"
                                        >
                                          <Circle className="h-4 w-4" />
                                          Reset to Pending
                                        </DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Complete All Confirmation Dialog */}
        <Dialog open={showCompleteAllConfirmation} onOpenChange={setShowCompleteAllConfirmation}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Complete All Tasks
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Are you sure you want to mark all {tasksToComplete.length} incomplete tasks in "{categoryToComplete}" as completed?
                <br />
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCancelCompleteAll} className="flex-1">
                Cancel
              </Button>
              <Button type="button" variant="default" onClick={handleConfirmCompleteAll} className="flex-1 bg-green-600 hover:bg-green-700">
                Complete All
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>

      {/* Add Tasks Dialog */}

    </Dialog>
  );
}; 